/**
 * Backtest — the walk-forward replay, run on this device.
 *
 * **It runs client-side because it cannot run anywhere else.** A pass rebuilds the propensity model
 * every 5 matches, so MLS alone is ~110 model builds over ~800 fixtures — tens of milliseconds of
 * CPU per league against a Workers free-plan budget of ~10ms. Measured, recorded in DECISIONS.md,
 * and the reason the statistics live in `app/src/stats` rather than in the Worker.
 *
 * **Per league, never pooled.** Brazil averages 2.56 goals a game and MLS 3.09; a combined run
 * prices both against a blended base rate and washes out whatever signal exists in either.
 *
 * The first thing to read on this screen is the baseline row. **Every "every match" strategy must
 * return exactly 0.0%** — bets settle at each market's observed base rate, so betting all of them
 * breaks even by construction. A non-zero baseline means prices are being computed off a different
 * population than the matches evaluated, and every number below it is inflated.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { api, describeError } from '@/api';
import { LEAGUES } from '@/data/leagues';
import { fetchResults } from '@/data/matches';
import { Chip, Empty } from '@/screens/Upcoming';
import { useStore } from '@/store';
import {
  baselinesIntact,
  edgePoints,
  hitRate,
  isUnderSampled,
  liftPoints,
  roi,
  runBacktest,
  strategyZ,
  type BacktestRun,
  type StrategyResult,
} from '@/stats/backtest';
import * as React from 'react';
import { ScrollView, View } from 'react-native';

/** The strategy keys whose rows gate the live flags. These are what get written back. */
const GATE_SIGNALS = [
  'btts_flagged',
  'over_abs',
  'under_abs',
  'bttsno_model',
  'home_model',
  'draw_model',
  'away_model',
];

export function BacktestScreen() {
  const { leagues, resultsByLeague } = useStore();
  const [code, setCode] = React.useState<string>('mls');
  const [run, setRun] = React.useState<BacktestRun | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState('Pick a league and replay every rule over its stored results.');

  // Leagues with enough history to be worth offering, biggest first.
  const options = React.useMemo(() => {
    const withStats = leagues.filter((l) => (l.stats?.matches ?? 0) > 0);
    return (withStats.length ? withStats : LEAGUES.map((l) => ({ ...l, stats: undefined as never })))
      .slice()
      .sort((a, b) => (b.stats?.matches ?? 0) - (a.stats?.matches ?? 0));
  }, [leagues]);

  const go = React.useCallback(async () => {
    setBusy(true);
    setNote('Loading results…');
    try {
      const results = resultsByLeague.get(code) ?? (await fetchResults(code));
      if (results.length === 0) {
        setRun(null);
        setNote('No stored results for this league. Backfill it from Leagues first.');
        return;
      }
      setNote(`Replaying ${results.length} matches…`);
      // Yield a frame so the note paints before the synchronous replay blocks the thread.
      await new Promise((r) => setTimeout(r, 16));
      const out = runBacktest(results, 10);
      setRun(out);
      setNote(
        out.matchesScored === 0
          ? `Only ${results.length} results — under the warmup, so nothing was evaluated.`
          : `${out.matchesScored} of ${out.matchesTotal} evaluated after a ${out.warmupMatches}-match warmup.`,
      );
    } catch (e) {
      setNote(describeError(e));
    } finally {
      setBusy(false);
    }
  }, [code, resultsByLeague]);

  /** Writes the seven gate signals back, so `trader/` reads the same evidence this screen showed. */
  const save = React.useCallback(async () => {
    if (!run) return;
    const rows = run.strategies
      .filter((s) => GATE_SIGNALS.includes(s.key))
      .map((s) => ({
        signal: s.key,
        bets: s.bets,
        hitRate: hitRate(s),
        edgePoints: edgePoints(s),
        zScore: strategyZ(s),
      }));
    try {
      await api.put(`/api/scorecards/${encodeURIComponent(code)}`, { rows });
      setNote(`Saved ${rows.length} gate signals for ${code}. The trader reads these.`);
    } catch (e) {
      setNote(describeError(e));
    }
  }, [run, code]);

  return (
    <ScrollView contentContainerClassName="gap-3 pb-6">
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerClassName="gap-1 pb-1">
        {options.map((l) => (
          <Chip
            key={l.code}
            label={`${l.displayName}${l.stats ? ` (${l.stats.matches})` : ''}`}
            active={code === l.code}
            onPress={() => setCode(l.code)}
          />
        ))}
      </ScrollView>

      <View className="flex-row items-center gap-2">
        <Button size="sm" disabled={busy} onPress={() => void go()}>
          <Text>{busy ? 'Running…' : 'Run backtest'}</Text>
        </Button>
        <Button variant="outline" size="sm" disabled={!run} onPress={() => void save()}>
          <Text>Save gate signals</Text>
        </Button>
      </View>

      <Text variant="small" className="text-muted-foreground">
        {note}
      </Text>

      {run ? <Report run={run} /> : <Empty title="No run yet" body="Pick a league and press Run backtest. It replays every rule walk-forward on this device." />}
    </ScrollView>
  );
}

function Report({ run }: { run: BacktestRun }) {
  const intact = baselinesIntact(run);
  const judgeable = run.strategies.filter((s) => s.hasBreakEven && !isUnderSampled(s)).length;

  return (
    <View className="gap-3">
      {/* The first thing to read. If this is red, nothing below it is real. */}
      <Card>
        <CardContent className="gap-1 py-3">
          <Text
            variant="small"
            className={cn(intact ? 'text-success' : 'text-destructive font-semibold')}>
            {intact
              ? 'Baselines return 0.0% — pricing is sound.'
              : "BASELINE DRIFT: an 'every match' row is non-zero. Prices are being computed off a different population than the matches evaluated, so everything below is inflated."}
          </Text>
          <Text variant="small" className="text-muted-foreground">
            Propensity lift: flagged {run.flaggedHitRate.toFixed(1)}% vs unflagged{' '}
            {run.unflaggedHitRate.toFixed(1)}% (lift {liftPoints(run) >= 0 ? '+' : ''}
            {liftPoints(run).toFixed(1)}, z = {run.zScore.toFixed(2)}) ·{' '}
            {run.flagRate.toFixed(1)}% of matches flagged
          </Text>
          <Text variant="small" className="text-muted-foreground">
            {Math.abs(run.zScore) >= 1.96
              ? 'Statistically significant at 5% — the ranking carries real information.'
              : 'NOT statistically significant — this lift is still within noise. Advisory only; re-run as data grows.'}
          </Text>
          <Text variant="caption" className="text-muted-foreground">
            {judgeable} judgeable strategies tested — at a 5% bar about {(judgeable * 0.05).toFixed(1)}{' '}
            will look significant by chance alone. Treat z ≥ 2.5, or replication on matches that
            settle after today, as the real threshold.
          </Text>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <Text variant="heading">Quintiles by propensity</Text>
        </CardHeader>
        <CardContent className="py-3">
          <View className="flex-row flex-wrap gap-2">
            {run.quintileCounts.map((n, q) => (
              <View key={q} className="border-border min-w-[86px] flex-1 rounded-lg border p-2">
                <Text variant="caption" className="text-muted-foreground">
                  Q{q + 1} ({n})
                </Text>
                <Text variant="small">
                  {n > 0 ? `${((100 * run.quintileHits[q]) / n).toFixed(1)}%` : '—'}
                </Text>
              </View>
            ))}
          </View>
        </CardContent>
      </Card>

      {run.strategies.map((s) => (
        <StrategyRow key={s.key} s={s} />
      ))}
    </View>
  );
}

function StrategyRow({ s }: { s: StrategyResult }) {
  const e = edgePoints(s);
  const r = roi(s);
  const z = strategyZ(s);
  const baseline = s.name.includes('every match');
  const thin = isUnderSampled(s);

  return (
    <Card>
      <CardContent className="gap-1 py-2.5">
        <View className="flex-row items-start justify-between gap-2">
          <View className="min-w-0 flex-1">
            <Text variant="small" className="font-medium">
              {s.name}
            </Text>
            <Text variant="caption" className="text-muted-foreground">
              {s.description}
            </Text>
          </View>
          {baseline ? (
            <Badge variant={Math.abs(r) < 0.05 ? 'success' : 'destructive'}>
              <Text>{r.toFixed(1)}%</Text>
            </Badge>
          ) : (
            <Badge variant={thin ? 'outline' : r > 0 ? 'success' : 'secondary'}>
              <Text>
                {r >= 0 ? '+' : ''}
                {r.toFixed(1)}%
              </Text>
            </Badge>
          )}
        </View>
        <Text variant="caption" className="text-muted-foreground">
          {s.bets} bets · {hitRate(s).toFixed(1)}% hit
          {s.hasBreakEven ? ` vs ${(s.price * 100).toFixed(1)}% break-even · edge ${e >= 0 ? '+' : ''}${e.toFixed(1)} · z ${z >= 0 ? '+' : ''}${z.toFixed(2)}` : ' · no single break-even'}
          {' · '}worst drawdown −${s.maxDrawdown.toFixed(0)} · {s.longestLosingStreak}L streak
        </Text>
        {thin && !baseline ? (
          <Text variant="caption" className="text-warning">
            only {s.bets} bets — too few to judge
          </Text>
        ) : null}
      </CardContent>
    </Card>
  );
}
